
import { PropType, defineComponent } from 'vue'
import { uniqueId, debounce } from 'lodash'
import Micromodal from 'micromodal'
import NrButton from '../NrButton/nr-button.vue'
import NrIconButton from '../NrIconButton/nr-icon-button.vue'

type ModalSize = 'small' | 'medium' | 'large'
type ModalTypes = 'default' | 'warning'

interface Data {
  id: string
  showScrollbarMarker: boolean
  [key: string]: unknown
}

export default defineComponent({
  name: 'NrModal',
  components: { NrButton, NrIconButton },
  props: {
    size: {
      type: String as PropType<ModalSize>,
      default: 'medium'
    },
    show: {
      type: Boolean,
      default: false
    },
    showClose: {
      type: Boolean,
      default: false
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    closeOnEsc: {
      type: Boolean,
      default: true
    },
    closeOnOverlayClick: {
      type: Boolean,
      default: true
    },
    hasSecondaryButton: {
      type: Boolean,
      default: false
    },
    primaryButtonText: {
      type: String,
      default: 'Close'
    },
    secondaryButtonText: {
      type: String,
      default: 'Cancel'
    },
    disableButtons: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Modal title'
    },
    type: {
      type: String as PropType<ModalTypes>,
      default: 'default'
    }
  },
  data(): Data {
    return {
      id: uniqueId('nr-modal-'),
      showScrollbarMarker: false
    }
  },
  computed: {
    modalContentClassnames(): { [key: string]: boolean } {
      return {
        'nr-modal__content': true,
        'nr-modal__content--scrollbar': this.showScrollbarMarker
      }
    },
    modalClassnames(): { [key: string]: boolean } {
      return {
        'nr-modal__container': true,
        [`nr-modal__container--${this.size}`]: true
      }
    }
  },
  created() {
    this.onResize = debounce(this.onResize, 400)
  },
  mounted() {
    this.show && this.toggleModal(this.show)
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() {
    // Normally `toggleModal` is called when `show` changes, but if this
    // component is unmounted while `show` is true we need to call it here.
    // Otherwise, `Micromodal.close` is never called.
    if (this.show) {
      this.toggleModal(false)
    }
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('keydown', this.disableCloseOnEsc)
  },
  methods: {
    onResize() {
      const element = this.$refs['modal-content'] as HTMLElement | undefined

      if (!element) {
        return
      }

      this.showScrollbarMarker = element.scrollHeight > element.clientHeight
    },
    toggleModal(state: boolean) {
      if (state) {
        this.$nextTick(() => {
          document.addEventListener('keydown', this.disableCloseOnEsc)
          Micromodal.show(this.id, {
            openClass: 'nr-modal--open',
            disableScroll: true,
            disableFocus: true,
            onClose: () => this.$emit('on-close-click')
          })
        })
      } else {
        Micromodal.close(this.id)
        document.removeEventListener('keydown', this.disableCloseOnEsc)
      }
    },
    disableCloseOnEsc(event?: KeyboardEvent) {
      if (this.closeOnEsc) {
        return
      }

      if (/Esc/.test(event?.key || '')) {
        event?.stopImmediatePropagation()
      }
    },
    handlePrimaryClick(event?: MouseEvent) {
      this.$emit('on-primary-click', event)
    },
    handleSecondaryClick(event?: MouseEvent) {
      this.$emit('on-secondary-click', event)
    },
    handleCloseClick(event?: Event) {
      this.$emit('on-close-click', event)
    },
    handleOverlayClick(event: MouseEvent) {
      if (this.closeOnOverlayClick && this.$refs.overlay === event.target) {
        this.handleCloseClick(event)
      }
    }
  },
  watch: {
    show(next) {
      this.toggleModal(next)
    }
  }
})
